import { useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDate } from '@/lib/utils'
import { submitDownloadForm } from '@/lib/http'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

type Staff = {
  id: string
  email_address: string
  compliance: boolean
  compliance_accepts_assignments?: boolean
  support: boolean
  support_accepts_assignments?: boolean
}

type AssignmentRow = {
  id: string
  assignment_id: string
  assignment_role: 'compliance' | 'support'
  record_type: 'business' | 'formation'
  record_id: string
  business_id: string
  name: string
  status: string
  account_name: string
  current_compliance_officer_email: string | null
  current_support_officer_email: string | null
  created_at: string
  assigned_at: string
  href: string
}

type StaffOption = {
  id: string
  email_address: string
}

type Counts = {
  total: number
  business: number
  formation: number
  compliance: number
  support: number
}

type Props = {
  staff: Staff
  assignments: AssignmentRow[]
  filters: {
    search: string
    role: 'either' | 'compliance' | 'support'
    record_type: 'all' | 'business' | 'formation'
  }
  counts: Counts
  eligibleComplianceStaff: StaffOption[]
  eligibleSupportStaff: StaffOption[]
  pagination: PaginationMeta
}

export default function StaffAssignmentsIndex({
  staff,
  assignments,
  filters,
  counts,
  eligibleComplianceStaff,
  eligibleSupportStaff,
  pagination,
}: Props) {
  const confirm = useConfirm()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [mode, setMode] = useState<'manual' | 'automatic'>('automatic')
  const [complianceIdentityId, setComplianceIdentityId] = useState('')
  const [supportIdentityId, setSupportIdentityId] = useState('')
  const [search, setSearch] = useState(filters.search)
  const [selectionMode, setSelectionMode] = useState<'page' | 'all_matching'>('page')
  const [exportDialogOpen, setExportDialogOpen] = useState(false)

  const selectedRows = useMemo(
    () => assignments.filter(assignment => selectedIds.includes(assignment.id)),
    [assignments, selectedIds],
  )
  const selectedComplianceCount = selectedRows.filter(row => row.assignment_role === 'compliance').length
  const selectedSupportCount = selectedRows.filter(row => row.assignment_role === 'support').length
  const selectedBusinessCount = selectedRows.filter(row => row.record_type === 'business').length
  const selectedFormationCount = selectedRows.filter(row => row.record_type === 'formation').length
  const effectiveSelectedCount = selectionMode === 'all_matching' ? counts.total : selectedRows.length
  const effectiveComplianceCount = selectionMode === 'all_matching' ? counts.compliance : selectedComplianceCount
  const effectiveSupportCount = selectionMode === 'all_matching' ? counts.support : selectedSupportCount
  const effectiveBusinessCount = selectionMode === 'all_matching' ? counts.business : selectedBusinessCount
  const effectiveFormationCount = selectionMode === 'all_matching' ? counts.formation : selectedFormationCount
  const allPageSelected = assignments.length > 0 && assignments.every(assignment => selectedIds.includes(assignment.id))
  const manualReady =
    effectiveSelectedCount > 0 &&
    (effectiveComplianceCount === 0 || complianceIdentityId.length > 0) &&
    (effectiveSupportCount === 0 || supportIdentityId.length > 0)
  const canSubmit = mode === 'automatic' ? effectiveSelectedCount > 0 : manualReady

  function setReassignmentMode(value: 'manual' | 'automatic') {
    setMode(value)
    if (value === 'automatic') {
      setComplianceIdentityId('')
      setSupportIdentityId('')
    }
  }

  function RoleLabel({ role }: { role: 'compliance' | 'support' }) {
    const short = role === 'compliance' ? 'CO' : 'SO'
    const label = role === 'compliance' ? 'Compliance Officer' : 'Support Officer'

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className="cursor-help text-[10px]" aria-label={label}>{short}</Badge>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    )
  }

  function applyFilters(next: Partial<Props['filters']>) {
    const merged = { ...filters, ...next }
    router.get(
      `/backoffice/staff/${staff.id}/assignments`,
      {
        search: merged.search || undefined,
        role: merged.role === 'either' ? undefined : merged.role,
        record_type: merged.record_type === 'all' ? undefined : merged.record_type,
      },
      { preserveState: true, preserveScroll: true, replace: true },
    )
    setSelectedIds([])
    setSelectionMode('page')
  }

  function toggleAllPage() {
    if (allPageSelected) {
      setSelectedIds(ids => ids.filter(id => !assignments.some(assignment => assignment.id === id)))
    } else {
      setSelectedIds(ids => Array.from(new Set([...ids, ...assignments.map(assignment => assignment.id)])))
      setSelectionMode('page')
    }
  }

  function handleExport() {
    setExportDialogOpen(false)
    submitDownloadForm(`/backoffice/staff/${staff.id}/assignments/export_contacts`, {
      search: filters.search,
      role: filters.role === 'either' ? '' : filters.role,
      record_type: filters.record_type === 'all' ? '' : filters.record_type,
    })
  }

  async function submit() {
    if (!canSubmit) return
    const ok = await confirm({
      title: mode === 'automatic' ? 'Redistribute assignments' : 'Reassign assignments',
      description: `${effectiveSelectedCount} assignments selected: ${effectiveBusinessCount} business, ${effectiveFormationCount} formation, ${effectiveComplianceCount} CO, ${effectiveSupportCount} SO.`,
      confirmLabel: mode === 'automatic' ? 'Redistribute' : 'Reassign',
      destructive: true,
    })
    if (!ok) return

    router.post(
      `/backoffice/staff/${staff.id}/assignment_reassignment`,
      {
        mode,
        selection_mode: selectionMode,
        search: filters.search || undefined,
        role: filters.role === 'either' ? undefined : filters.role,
        record_type: filters.record_type === 'all' ? undefined : filters.record_type,
        compliance_identity_id: mode === 'manual' ? complianceIdentityId || undefined : undefined,
        support_identity_id: mode === 'manual' ? supportIdentityId || undefined : undefined,
        assignments: selectedRows.map(row => ({
          assignment_id: row.assignment_id,
          assignment_role: row.assignment_role,
          record_type: row.record_type,
          record_id: row.record_id,
        })),
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSelectedIds([])
          setSelectionMode('page')
        },
      },
    )
  }

  return (
    <BackofficeLayout
      title="Staff Assignments"
      contentWidth="full"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Staff', href: '/backoffice/staff' },
        { label: staff.email_address },
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Assignments for {staff.email_address}</h1>
            <p className="text-sm text-muted-foreground">
              Review and reassign business and formation work without changing backoffice access.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant="outline"
              className={staff.compliance_accepts_assignments ? 'border-green-200 bg-green-100 text-green-700' : 'border-red-200 bg-red-100 text-red-700'}
            >
              CO {staff.compliance ? (staff.compliance_accepts_assignments ? 'active' : 'inactive') : 'none'}
            </Badge>
            <Badge
              variant="outline"
              className={staff.support_accepts_assignments ? 'border-green-200 bg-green-100 text-green-700' : 'border-red-200 bg-red-100 text-red-700'}
            >
              SO {staff.support ? (staff.support_accepts_assignments ? 'active' : 'inactive') : 'none'}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => setExportDialogOpen(true)}
              disabled={counts.total === 0}
            >
              <Download className="w-3.5 h-3.5" />
              Export Contacts
            </Button>
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <div className="rounded-lg border p-2.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</p>
            <p className="text-base font-semibold">{counts.total}</p>
          </div>
          <div className="rounded-lg border p-2.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Businesses</p>
            <p className="text-base font-semibold">{counts.business}</p>
          </div>
          <div className="rounded-lg border p-2.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Formations</p>
            <p className="text-base font-semibold">{counts.formation}</p>
          </div>
          <div className="rounded-lg border p-2.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} aria-label="Compliance Officer" className="cursor-help underline decoration-dotted underline-offset-2">CO</span>
                </TooltipTrigger>
                <TooltipContent>Compliance Officer</TooltipContent>
              </Tooltip>
            </p>
            <p className="text-base font-semibold">{counts.compliance}</p>
          </div>
          <div className="rounded-lg border p-2.5">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} aria-label="Support Officer" className="cursor-help underline decoration-dotted underline-offset-2">SO</span>
                </TooltipTrigger>
                <TooltipContent>Support Officer</TooltipContent>
              </Tooltip>
            </p>
            <p className="text-base font-semibold">{counts.support}</p>
          </div>
        </div>

        <div className="rounded-lg border p-3">
          <div className="grid gap-3 lg:grid-cols-[1fr_160px_160px_auto]">
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') applyFilters({ search })
              }}
              placeholder="Search assignments..."
              className="h-9"
            />
            <Select value={filters.role} onValueChange={value => applyFilters({ role: value as Props['filters']['role'] })}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="either">Either role</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="support">Support</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.record_type} onValueChange={value => applyFilters({ record_type: value as Props['filters']['record_type'] })}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="business">Businesses</SelectItem>
                <SelectItem value="formation">Formations</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => applyFilters({ search })}>Search</Button>
          </div>
        </div>

        <div className="rounded-lg border">
          <div className="flex flex-col gap-3 border-b p-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Checkbox checked={allPageSelected} onCheckedChange={toggleAllPage} aria-label="Select all assignments on this page" />
              <span className="text-sm text-muted-foreground">{effectiveSelectedCount} selected</span>
              {selectedIds.length > 0 && selectionMode === 'page' && counts.total > assignments.length && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSelectionMode('all_matching'); setSelectedIds([]) }}>
                  Select all {counts.total} matching
                </Button>
              )}
              {effectiveSelectedCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSelectedIds([]); setSelectionMode('page') }}>
                  Clear
                </Button>
              )}
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:flex xl:items-center">
              <Select value={mode} onValueChange={value => setReassignmentMode(value as 'manual' | 'automatic')}>
                <SelectTrigger className="h-8 w-full xl:w-40" aria-label="Reassignment mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="automatic">Automatic</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>

              {mode === 'manual' && effectiveComplianceCount > 0 && (
                <Select value={complianceIdentityId} onValueChange={setComplianceIdentityId}>
                  <SelectTrigger className="h-8 w-full xl:w-64">
                    <SelectValue placeholder="Replacement CO" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleComplianceStaff.map(option => (
                      <SelectItem key={option.id} value={option.id}>{option.email_address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {mode === 'manual' && effectiveSupportCount > 0 && (
                <Select value={supportIdentityId} onValueChange={setSupportIdentityId}>
                  <SelectTrigger className="h-8 w-full xl:w-64">
                    <SelectValue placeholder="Replacement SO" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleSupportStaff.map(option => (
                      <SelectItem key={option.id} value={option.id}>{option.email_address}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button size="sm" className="h-8" disabled={!canSubmit} onClick={submit}>
                {mode === 'automatic' ? 'Redistribute' : 'Reassign'}
              </Button>
            </div>
          </div>

          {assignments.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">No assignments match these filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="hidden min-w-[1080px] grid-cols-[88px_minmax(300px,1fr)_110px_82px_96px_minmax(190px,230px)_106px] items-center border-b bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:grid">
                <div>Selection</div>
                <div>Business or formation name</div>
                <div>Record type</div>
                <div>Role</div>
                <div>Status</div>
                <div>Current assignee</div>
                <div className="text-right">Created</div>
              </div>
              <div className="divide-y">
              {assignments.map(assignment => (
                <div key={assignment.id} className="grid gap-2 p-3 lg:min-w-[1080px] lg:grid-cols-[88px_minmax(300px,1fr)_110px_82px_96px_minmax(190px,230px)_106px] lg:items-center lg:py-2.5">
                  <div>
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Selection</div>
                    <Checkbox
                      checked={selectedIds.includes(assignment.id)}
                      onCheckedChange={checked => {
                        setSelectedIds(ids => checked ? [...ids, assignment.id] : ids.filter(id => id !== assignment.id))
                      }}
                      aria-label={`Select ${assignment.name}`}
                      className="size-5 lg:size-4"
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Business or formation name</div>
                    <Link
                      href={assignment.href}
                      title={assignment.name}
                      className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-sm font-medium hover:underline"
                    >
                      {assignment.name}
                    </Link>
                    <p className="mt-0.5 max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground" title={assignment.account_name}>
                      {assignment.account_name}
                    </p>
                  </div>

                  <div>
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Record type</div>
                    <Badge variant="outline" className="text-[10px] capitalize">{assignment.record_type}</Badge>
                  </div>

                  <div>
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Assignment role</div>
                    <RoleLabel role={assignment.assignment_role} />
                  </div>

                  <div>
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Status</div>
                    <span className="text-xs capitalize text-muted-foreground">{assignment.status}</span>
                  </div>

                  <div className="min-w-0 space-y-0.5 text-xs text-muted-foreground">
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Current assignee</div>
                    <div className="truncate" title={assignment.current_compliance_officer_email || undefined}>
                      CO: <span className="text-foreground">{assignment.current_compliance_officer_email || '-'}</span>
                    </div>
                    <div className="truncate" title={assignment.current_support_officer_email || undefined}>
                      SO: <span className="text-foreground">{assignment.current_support_officer_email || '-'}</span>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground lg:text-right">
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground lg:hidden">Created</div>
                    {formatDate(assignment.created_at)}
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>

        <Pagination pagination={pagination} />
      </div>

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Customer Contacts</DialogTitle>
            <DialogDescription>
              This will export customer contact information for {counts.total} matching record{counts.total !== 1 ? 's' : ''}:{' '}
              {counts.business} business{counts.business !== 1 ? 'es' : ''}, {counts.formation} formation{counts.formation !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3 text-sm">
            <div className="text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Active filters:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {filters.search && <li>Search: "{filters.search}"</li>}
                <li>Role: {filters.role === 'either' ? 'Either' : filters.role === 'compliance' ? 'Compliance Officer' : 'Support Officer'}</li>
                <li>Record type: {filters.record_type === 'all' ? 'All records' : filters.record_type === 'business' ? 'Businesses only' : 'Formations only'}</li>
              </ul>
            </div>
            <div className="text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Columns included:</p>
              <p>Record type, business/formation name, workspace name, customer owner name, customer owner email, assigned CO, assigned SO, status, record ID.</p>
            </div>
            <div className="text-amber-700 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2">
              This export contains customer contact information. Handle responsibly and in accordance with your data handling policies.
            </div>
            {counts.total === 0 && (
              <p className="text-destructive text-sm">No records match the current filters.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={counts.total === 0}>
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BackofficeLayout>
  )
}
